import React, { useEffect, useState, lazy } from 'react';
import { MapPinIcon, LanguagesIcon, UsersIcon, ImageIcon, CalendarIcon, CheckSquareIcon, ChevronDownIcon, ChevronUpIcon, XIcon, MaximizeIcon } from 'lucide-react';
import { ImageData } from '../utils/mockData';
interface TestDetailsProps {
  data: ImageData[];
}
export const TestDetails: React.FC<TestDetailsProps> = ({
  data
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);
  const locations = ['Netherlands', 'Germany', 'United Kingdom', 'France', 'Poland', 'Italy'];
  const getVisibleItemCount = () => {
    if (windowWidth >= 1024) {
      return locations.length;
    } else if (windowWidth >= 768) {
      return 8;
    } else {
      return 4;
    }
  };
  const visibleLocations = showAllLocations ? locations : locations.slice(0, getVisibleItemCount());
  const hasMoreLocations = locations.length > getVisibleItemCount();
  const languages = ['English', 'German', 'Dutch', 'French', 'Polish', 'Italian'];
  return <div className="space-y-8" data-id="element-454">
      {/* Introduction */}
      <div className="flex items-start space-x-4 bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-455">
        <div className="space-y-3" data-id="element-456">
          <h3 className="font-medium text-[#101010]" data-id="element-457">Test Overview</h3>
          <p className="text-base text-[#404040] leading-relaxed" data-id="element-458">
            This report details the findings of a visual asset pre-test
            conducted by Brandpulse for Lenovo EMEA. The primary objective was
            to determine how the same creative directions perform across two
            distinct audience segments: a tech enthusiast audience and a
            lifestyle audience. Four creative variants, two lifestyle-led and
            two product-led, were served to both segments under identical
            conditions. We leverage passive behavioural measurement to identify
            which creative wins within which audience, utilising audience
            signals, engagement points and a Stopping Power Rating (SPR) per
            segment. The outcome is evidence-based guidance on matching creative
            direction to the audience that actually buys.
          </p>
        </div>
      </div>

      {/* Test Info Blocks */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-459">
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-460">
          <div className="flex items-center space-x-2 mb-4" data-id="element-461">
            <ImageIcon className="h-5 w-5 text-[#C00000]" data-id="element-462" />
            <h3 className="font-medium text-[#101010]" data-id="element-463">Type</h3>
          </div>
          <p className="text-sm text-[#737373]" data-id="element-464">
            Attention Pre-Test · Two-Audience Comparison
          </p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-465">
          <div className="flex items-center space-x-2 mb-4" data-id="element-466">
            <CalendarIcon className="h-5 w-5 text-[#C00000]" data-id="element-467" />
            <h3 className="font-medium text-[#101010]" data-id="element-468">Test period</h3>
          </div>
          <p className="text-sm text-[#737373]" data-id="element-469">7 days</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-470">
          <div className="flex items-center space-x-2 mb-4" data-id="element-471">
            <CheckSquareIcon className="h-5 w-5 text-[#C00000]" data-id="element-472" />
            <h3 className="font-medium text-[#101010]" data-id="element-473">Completion date</h3>
          </div>
          <p className="text-sm text-[#737373]" data-id="element-474">July 2026</p>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-475">
        <h3 className="font-medium text-[#101010] mb-4" data-id="element-476">Test Assets</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-477">
          {data.map(item => <div key={item.id} className="relative group cursor-pointer" onClick={() => setSelectedImage(item.thumbnailUrl)} data-id="element-478">
              <div className="w-full aspect-video rounded-sm overflow-hidden bg-[#F9FAFB] border border-[#E5E5E5]" data-id="element-479">
                <img src={item.thumbnailUrl} alt={item.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" data-id="element-480" />
              </div>
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-sm" data-id="element-481" />
              <div className="absolute top-2 right-2 bg-white/80 p-1.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-482">
                <MaximizeIcon className="h-4 w-4 text-[#101010]" data-id="element-483" />
              </div>
              <p className="text-xs text-[#101010] font-medium mt-2" data-id="element-484">
                {item.name}
              </p>
              <p className="text-xs text-[#737373] mt-1" data-id="element-485">{item.description}</p>
            </div>)}
        </div>
      </div>

      {/* Targeting Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-486">
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-487">
          <div className="flex items-center space-x-2 mb-4" data-id="element-488">
            <MapPinIcon className="h-5 w-5 text-[#C00000]" data-id="element-489" />
            <h3 className="font-medium text-[#101010]" data-id="element-490">Location Targeting</h3>
          </div>
          <div className="flex flex-col" data-id="element-491">
            <p className="text-sm text-[#101010] font-medium mb-2" data-id="element-492">
              Key Lenovo EMEA markets
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-493">
              {visibleLocations.map(location => <span key={location} className="px-2 py-1 bg-[#F9FAFB] border border-[#E5E5E5] rounded-sm text-xs text-[#737373]" data-id="element-494">
                  {location}
                </span>)}
            </div>
            {hasMoreLocations && <button onClick={() => setShowAllLocations(!showAllLocations)} className="mt-3 text-xs font-medium text-[#C00000] hover:text-[#990000] flex items-center transition-colors self-start" data-id="element-495">
                {showAllLocations ? <>
                    Show less <ChevronUpIcon className="h-3 w-3 ml-1" data-id="element-496" />
                  </> : <>
                    +{locations.length - getVisibleItemCount()} more locations{' '}
                    <ChevronDownIcon className="h-3 w-3 ml-1" data-id="element-497" />
                  </>}
              </button>}
          </div>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-498">
          <div className="flex items-center space-x-2 mb-4" data-id="element-499">
            <LanguagesIcon className="h-5 w-5 text-[#C00000]" data-id="element-500" />
            <h3 className="font-medium text-[#101010]" data-id="element-501">Languages</h3>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-502">
            {languages.map(language => <span key={language} className="px-2 py-1 bg-[#F9FAFB] border border-[#E5E5E5] rounded-sm text-xs text-[#737373]" data-id="element-503">
                {language}
              </span>)}
          </div>
        </div>
      </div>

      {/* Audience Segments */}
      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-504">
        <div className="flex items-center space-x-2 mb-4" data-id="element-505">
          <UsersIcon className="h-5 w-5 text-[#C00000]" data-id="element-506" />
          <h3 className="font-medium text-[#101010]" data-id="element-507">Audience Segments</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-508">
          <div className="p-4 bg-[#F9FAFB] border border-[#E5E5E5] rounded-sm" data-id="element-509">
            <h4 className="font-medium text-[#101010] mb-2" data-id="element-510">
              Tech enthusiast audience
            </h4>
            <p className="text-sm text-[#404040]" data-id="element-511">
              25-54, interests Computers & Peripherals, Software, affinity
              Technology
            </p>
          </div>
          <div className="p-4 bg-[#F9FAFB] border border-[#E5E5E5] rounded-sm" data-id="element-512">
            <h4 className="font-medium text-[#101010] mb-2" data-id="element-513">
              Lifestyle audience
            </h4>
            <p className="text-sm text-[#404040]" data-id="element-514">
              25-54, affinity Lifestyles & Hobbies, Shoppers, interests Clothing
              & Accessories
            </p>
          </div>
        </div>
        <p className="text-sm text-[#737373] mt-4 italic" data-id="element-515">
          Both segments measured separately and independently, 30,000+ unique
          individuals per segment.
        </p>
      </div>

      {/* Demographics, Passion Profiles, Life Milestones, Intent Signals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-516">
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-517">
          <div className="flex items-center space-x-2 mb-4" data-id="element-518">
            <UsersIcon className="h-5 w-5 text-[#C00000]" data-id="element-519" />
            <h3 className="font-medium text-[#101010]" data-id="element-520">Demographics</h3>
          </div>
          <div className="space-y-4" data-id="element-521">
            <div data-id="element-522">
              <p className="text-sm font-medium text-[#101010]" data-id="element-523">Gender</p>
              <p className="text-sm text-[#737373]" data-id="element-524">Male, Female</p>
            </div>
            <div data-id="element-525">
              <p className="text-sm font-medium text-[#101010]" data-id="element-526">Age Groups</p>
              <div className="flex flex-wrap gap-2 mt-2" data-id="element-527">
                {['25-34', '35-44', '45-54'].map(age => <span key={age} className="px-2 py-1 bg-[#F9FAFB] border border-[#E5E5E5] rounded-sm text-xs text-[#737373]" data-id="element-528">
                    {age}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-529">
          <h3 className="font-medium text-[#101010] mb-2" data-id="element-530">Life Events</h3>
          <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-sm text-sm" data-id="element-531">
            None
          </span>
        </div>
      </div>

      {/* Package Info */}
      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-532">
        <h3 className="font-medium text-[#101010] mb-2" data-id="element-533">Package Details</h3>
        <p className="text-sm text-[#737373]" data-id="element-534">
          Platinum Test Package. 60,000+ unique individuals total, 30,000+ per
          audience segment. Dual-segment design with independent statistical
          validation per segment and cross-segment comparison.
        </p>
      </div>

      {/* Image Lightbox */}
      {selectedImage && <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setSelectedImage(null)} data-id="element-535">
          <div className="relative w-full max-w-3xl flex items-center justify-center" data-id="element-536">
            <img src={selectedImage} alt="Test asset preview" className="max-w-full max-h-[90vh] rounded-sm shadow-2xl object-contain" referrerPolicy="no-referrer" onClick={e => e.stopPropagation()} data-id="element-537" />
            <button className="absolute -top-12 right-0 bg-white p-2 rounded-sm shadow-md hover:bg-gray-100 transition-colors" onClick={() => setSelectedImage(null)} aria-label="Close image" data-id="element-538">
              <XIcon className="h-5 w-5 text-gray-700" data-id="element-539" />
            </button>
          </div>
        </div>}
    </div>;
};